import React, { useState, useId, useEffect } from 'react';
import { Send, RotateCcw, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api.js';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Fn } from '../utils/rutValidation.js';

// Importación de Componentes
import SolicitanteForm from '../components/Solicitudes/SolicitanteForm';
import DocumentoItemForm from '../components/Solicitudes/DocumentoItemForm';
import DocumentosAdicionalesTable from '../components/Solicitudes/DocumentosAdicionalesTable';
import UploadArchivosAdjuntos from '../components/Solicitudes/UploadArchivosAdjuntos';
import PrivacyModal from '../components/Solicitudes/PrivacyModal';

const FORMAS_ENTREGA = ['Correo electrónico', 'Retiro en oficina', 'Correos de Chile'];

const TIPOS_DOCUMENTO = [
  'Copia de inscripción',
  'Copia con vigencia o Dominio Vigente',
  'Certificado hipotecas, gravámenes y prohibiciones (incluye copia y dominio vigente)',
  'Certificado de prenda agraria y prenda industrial',
  'Certificado de repertorios',
  'Vigencia de capital',
  'Vigencia de administración',
  'Certificado de litigios (incluye copia y dominio vigente)',
  'Copia de Documento Agregado',
];

const REGISTROS = [
  'Propiedad', 'Propiedad de aguas', 'Hipotecas y gravámenes', 
  'Prohibiciones e Interdicciones', 'Hipotecas y gravámenes de aguas',
  'Prohibiciones e interdicciones de aguas', 'Comercio',
  'Propiedad de minas', 'Descubrimiento de minas'
];

export const getRegistrosPorTipo = (tipoDoc) => {
  if (!tipoDoc) return REGISTROS;
  const t = tipoDoc.trim().toLowerCase();

  // 1. Certificado hipotecas, gravámenes y prohibiciones -> Propiedad y Propiedad de aguas
  if (t.includes('hipotecas, gravámenes y prohibiciones') || t.includes('hipotecas, gravamenes y prohibiciones')) {
    return ['Propiedad', 'Propiedad de aguas'];
  }
  // 2. Certificado de prenda agraria y prenda industrial -> Propiedad
  if (t.includes('prenda agraria') || t.includes('prenda industrial') || t.includes('prenda')) {
    return ['Propiedad'];
  }
  // 3. Vigencia de capital -> Comercio
  if (t.includes('vigencia de capital')) {
    return ['Comercio'];
  }
  // 4. Vigencia de administración -> Comercio
  if (t.includes('vigencia de administración') || t.includes('vigencia de administracion')) {
    return ['Comercio'];
  }
  // 5. Certificado de litigios (incluye copia y dominio vigente) -> Propiedad y Propiedad de aguas
  if (t.includes('litigios')) {
    return ['Propiedad', 'Propiedad de aguas'];
  }

  return REGISTROS;
};

const SOLICITANTE_VACIO = {
  rut_solicitante: '', nombre_solicitante: '', telefono: '',
  email: '', direccion: '', _hp_website_email: '',
  facturacion_misma_persona: true,
  rut_boleta: '', nombre_boleta: '', boleta_razon_social: '',
  boleta_giro: '', boleta_direccion: '', boleta_correo: ''
};

const ITEM_VACIO = {
  tipo_documento: TIPOS_DOCUMENTO[0], foja: '', vta: false,
  numero: '', anio: '', registro: '', nombre_propietario: '',
};

export default function Solicitudes() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const formId = useId();
  const [solicitante, setSolicitante] = useState(SOLICITANTE_VACIO);
  const [itemActual, setItemActual] = useState(ITEM_VACIO);
  const [items, setItems] = useState([]);
  const [entrega, setEntrega] = useState({ forma_entrega: FORMAS_ENTREGA[0], observaciones: '' });
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([]);

  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [aceptaPrivacidad, setAceptaPrivacidad] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSolicitante = (e) => {
    if (e.target.name === 'billing_update') {
      const b = e.target.value;
      setSolicitante(p => ({
        ...p,
        facturacion_misma_persona: b.facturacion_misma_persona,
        rut_boleta: b.boleta_rut,
        nombre_boleta: b.boleta_razon_social,
        boleta_razon_social: b.boleta_razon_social,
        boleta_giro: b.boleta_giro,
        boleta_direccion: b.boleta_direccion,
        boleta_correo: b.boleta_correo
      }));
    } else {
      setSolicitante((p) => ({ ...p, [e.target.name]: e.target.value }));
    }
  };

  const handleItem = (e) => {
    const { name, type, value, checked } = e.target;
    if (name === 'tipo_documento') {
      const allowed = getRegistrosPorTipo(value);
      setItemActual((p) => {
        const prevAllowed = getRegistrosPorTipo(p.tipo_documento);
        const keepCurrent = prevAllowed.length > 1 && allowed.includes(p.registro);
        const nuevoRegistro = allowed.length === 1 ? allowed[0] : (keepCurrent ? p.registro : '');
        return {
          ...p,
          tipo_documento: value,
          registro: nuevoRegistro
        };
      });
      return;
    }
    setItemActual((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEntrega = (e) => setEntrega((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    let validFiles = [];
    for (let file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`El archivo ${file.name} no debe exceder los 10MB.`);
        continue;
      }
      validFiles.push(file);
    }
    if (validFiles.length > 0) {
      setArchivosAdjuntos((prev) => [...prev, ...validFiles].slice(0, 5));
    }
    e.target.value = '';
  };

  const removeFile = (index) => setArchivosAdjuntos(prev => prev.filter((_, i) => i !== index));

  const isAgregado = itemActual.tipo_documento === 'Copia de Documento Agregado';

  const agregarItem = () => {
    if ((!isAgregado && !itemActual.foja) || !itemActual.numero || !itemActual.anio || !itemActual.registro) {
      toast.error('Completa los datos obligatorios para agregar el documento.');
      return;
    }
    setItems((prev) => [...prev, { ...itemActual, _id: Date.now() }]);
    setItemActual(ITEM_VACIO);
    toast.success('Documento agregado a la lista.');
  };

  const eliminarItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

  const limpiar = () => {
    setSolicitante(SOLICITANTE_VACIO);
    setItemActual(ITEM_VACIO);
    setItems([]);
    setEntrega({ forma_entrega: FORMAS_ENTREGA[0], observaciones: '' });
    setArchivosAdjuntos([]);
    setResultado(null);
    setAceptaPrivacidad(false);
  };

  const prepararEnvio = () => {
    const tieneItemPrincipal = (!isAgregado ? itemActual.foja : true) && itemActual.numero && itemActual.anio && itemActual.registro;

    if (!tieneItemPrincipal && items.length === 0) {
      toast.error('Completa los datos del documento (Foja, Número, Año y Registro).');
      return;
    }
    if (!solicitante.rut_solicitante || !solicitante.nombre_solicitante || !solicitante.email) {
      toast.error('Completa los datos del solicitante.');
      return;
    }
    if (!Fn.validaRut(solicitante.rut_solicitante)) {
      toast.error('El RUT del solicitante es inválido.');
      return;
    }

    const cleanRut = solicitante.rut_solicitante.replace(/[^0-9kK]/g, '');
    const rutNum = parseInt(cleanRut.slice(0, -1), 10);
    
    if (rutNum >= 50000000) {
      toast.error('El solicitante debe ser persona natural (RUT inferior a 50 millones). Para empresas, ingrese los datos correspondientes en la sección de facturación / boleta.');
      return;
    }

    if (!solicitante.facturacion_misma_persona) {
      if (!solicitante.rut_boleta || !solicitante.boleta_razon_social || !solicitante.boleta_correo || !solicitante.boleta_direccion) {
        toast.error('Todos los datos de facturación son obligatorios al desmarcar la casilla (RUT, Razón Social, Correo y Dirección Tributaria).');
        return;
      }
      if (!Fn.validaRut(solicitante.rut_boleta)) {
        toast.error('El RUT de facturación / boleta es inválido.');
        return;
      }
      if (Fn.esEmpresa(solicitante.rut_boleta) && !solicitante.boleta_giro?.trim()) {
        toast.error('El Giro Comercial es obligatorio para empresas en los datos de facturación.');
        return;
      }
    }

    setIsPrivacyModalOpen(true);
  };

  const confirmarYEnviar = async () => {
    if (!aceptaPrivacidad) {
      toast.error('Debes aceptar la Política de Privacidad.');
      return;
    }
    if (!executeRecaptcha) {
      toast.error('ReCAPTCHA no está listo aún.');
      return;
    }

    setEnviando(true);
    try {
      const recaptchaToken = await executeRecaptcha('solicitudes_submit');

      const normalizar = (r) => {
        if (!r) return r;
        let limpio = r.replace(/\./g, '').replace(/\s/g, '');
        if (!limpio.includes('-') && limpio.length > 1) {
          return limpio.slice(0, -1) + '-' + limpio.slice(-1);
        }
        return limpio;
      };

      const solicitanteNormalizado = {
        ...solicitante,
        rut_solicitante: normalizar(solicitante.rut_solicitante),
        rut_boleta: normalizar(solicitante.rut_boleta)
      };

      let todosLosItems = [...items];
      if ((!isAgregado ? itemActual.foja : true) && itemActual.numero && itemActual.anio && itemActual.registro) {
        todosLosItems.push(itemActual);
      }

      const formData = new FormData();

      Object.keys(solicitanteNormalizado).forEach(key => {
        const val = solicitanteNormalizado[key];
        if (val !== null && val !== undefined && val !== '') {
          formData.append(key, typeof val === 'boolean' ? (val ? '1' : '0') : val);
        }
      });

      Object.keys(entrega).forEach(key => formData.append(key, entrega[key]));

      const itemsProcesados = todosLosItems.map(({ _id, ...item }) => item);
      formData.append('items', JSON.stringify(itemsProcesados));

      archivosAdjuntos.forEach((file) => formData.append('archivos_adjuntos[]', file));

      const { data } = await api.post('/solicitudes', formData, {
        headers: {
          'X-Recaptcha-Token': recaptchaToken,
          'Content-Type': 'multipart/form-data',
        },
      });

      setResultado(data);
      toast.success('Solicitud enviada correctamente');
      setIsPrivacyModalOpen(false);
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || 'Error al enviar la solicitud';
      toast.error(msg);
      if (error.response?.data?.errors) {
        Object.values(error.response.data.errors).forEach(err => toast.error(err[0]));
      }
    } finally {
      setEnviando(false);
    }
  };

  if (resultado) {
    return (
      <div className="container" style={{ maxWidth: '700px', marginTop: '3rem', animation: 'fadeInUp 0.6s ease-out' }}>
        <div className="glass-card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
          <CheckCircle size={64} style={{ color: 'var(--success)', margin: '0 auto 1.5rem' }} />
          <h2 style={{ color: 'var(--primary-dark)', marginBottom: '1rem', fontSize: '1.8rem' }}>¡Solicitud Enviada con Éxito!</h2>
          <p style={{ fontSize: '1.1rem', color: 'var(--text-main)', marginBottom: '2rem' }}>
            Tu número de seguimiento es:<br />
            <strong style={{ fontSize: '1.5rem', color: 'var(--primary)', display: 'inline-block', marginTop: '0.5rem', padding: '0.5rem 1rem', background: 'var(--primary-light)', borderRadius: '8px' }}>
              {resultado.numero_seguimiento}
            </strong>
          </p>
          <p style={{ color: 'var(--text-muted)', marginBottom: '2.5rem', lineHeight: '1.6' }}>
            Hemos enviado un correo a <strong>{solicitante.email}</strong> con los detalles de tu solicitud.<br />
            Pronto nos comunicaremos contigo.
          </p>
          <button className="btn btn-primary" onClick={limpiar} style={{ minWidth: '200px' }}>
            Ingresar Nueva Solicitud
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '1000px', paddingBottom: '3rem' }}>
      <header className="page-header" style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 className="page-title">Solicitud de Copias y Certificados</h1>
        <p className="page-subtitle" style={{ maxWidth: '600px', margin: '0 auto' }}>
          Completa el siguiente formulario para solicitar copias de inscripciones, certificados de vigencia, hipotecas, entre otros documentos registrales.
        </p>
      </header>

      <form id={formId} onSubmit={(e) => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        
        {/* Paso 1: Solicitante */}
        <SolicitanteForm solicitante={solicitante} handleSolicitante={handleSolicitante} />

        {/* Paso 2: Documentos (Uno o varios) */}
        <div>

          <DocumentoItemForm 
            itemActual={itemActual}
            handleItem={handleItem}
            agregarItem={agregarItem}
            isAgregado={isAgregado}
            TIPOS_DOCUMENTO={TIPOS_DOCUMENTO}
            REGISTROS={REGISTROS}
            getRegistrosPorTipo={getRegistrosPorTipo}
          />

          <div className="glass-card" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
            <DocumentosAdicionalesTable items={items} eliminarItem={eliminarItem} />

            {/* Carga de Archivos Opcional */}
            <UploadArchivosAdjuntos 
              archivosAdjuntos={archivosAdjuntos}
              handleFileChange={handleFileChange}
              removeFile={removeFile}
            />
          </div>
        </div>

        {/* Paso 3: Entrega y Envío */}
        <div className="glass-card">
          <h3 className="section-title">Opciones de Entrega y Envío</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Forma de Entrega</label>
              <select name="forma_entrega" value={entrega.forma_entrega} onChange={handleEntrega}>
                {FORMAS_ENTREGA.map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Observaciones (Opcional)</label>
              <input name="observaciones" value={entrega.observaciones} onChange={handleEntrega} placeholder="Algún detalle adicional..." />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-outline" onClick={limpiar} style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <RotateCcw size={18} /> Limpiar Formulario
            </button>
            <button type="button" className="btn btn-primary" onClick={prepararEnvio} disabled={enviando} style={{ flex: '2 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '1.1rem', padding: '0.8rem' }}>
              <Send size={20} /> Proceder al Envío
            </button>
          </div>
          <p style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '1rem' }}>
            El sistema está protegido por reCAPTCHA y aplican la Política de Privacidad y Términos de Servicio de Google.
          </p>
        </div>
      </form>

      {/* Modal de Privacidad */}
      <PrivacyModal 
        isOpen={isPrivacyModalOpen}
        setIsOpen={setIsPrivacyModalOpen}
        aceptaPrivacidad={aceptaPrivacidad}
        setAceptaPrivacidad={setAceptaPrivacidad}
        enviando={enviando}
        confirmarYEnviar={confirmarYEnviar}
      />
    </div>
  );
}
